import { useCallback, useRef, useState } from 'react';

import { AlertTriangle, Check } from 'lucide-react';

import { cn } from '@src/components/ui';

/** Shared transient-toast primitive. One queue implementation backs both the
 * app-wide GlobalToaster (#162) and the Listings copy/mark-listed feedback
 * (#365 dedup) — previously two parallel copies.
 *
 * `useToastQueue(duration)` owns the toast list and auto-dismisses each entry
 * after `duration` ms. `push(message, variant)` appends one. `<ToastStack>`
 * renders the list; callers control z-index and (to stay pixel-identical with
 * their prior bespoke markup) whether a danger toast shows the warning icon. */
function useToastQueue(duration) {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(0);

  const push = useCallback((message, variant = 'success') => {
    if (!message) {
      return;
    }
    const id = nextId.current;
    nextId.current += 1;
    setToasts((previous) => [...previous, { id, message, variant }]);
    setTimeout(() => {
      setToasts((previous) => previous.filter((toast) => toast.id !== id));
    }, duration);
  }, [duration]);

  return { toasts, push };
}


/** Renders a stacked, bottom-centred list of toasts. `zIndexClassName` lets the
 * global surface sit above page-local ones (z-70 vs z-60), matching the prior
 * separate components. `dangerIcon` (default true) renders the AlertTriangle on
 * danger toasts; Listings opts out to preserve its icon-less danger styling. */
function ToastStack({ toasts, zIndexClassName = 'z-[70]', dangerIcon = true }) {
  if (!toasts.length) {
    return null;
  }
  return (
    <div
      className={cn(
        'pointer-events-none fixed bottom-5 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2',
        zIndexClassName,
      )}
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'flex items-center gap-2 rounded-lg border border-hairline-strong bg-surface-1 px-3.5 py-2.5',
            'text-[13px] font-medium text-ink shadow-lg',
            toast.variant === 'danger' && 'border-danger/40 text-danger',
          )}
        >
          {toast.variant === 'danger'
            ? (dangerIcon ? <AlertTriangle className="size-3.5 text-danger" /> : null)
            : <Check className="size-3.5 text-success" strokeWidth={3} />}

          {toast.message}
        </div>
      ))}
    </div>
  );
}

ToastStack.displayName = 'ToastStack';

export { ToastStack, useToastQueue };
